import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLoansEntry } from './hooks/useLoansEntry';
import AppDateInput from '../../../components/AppDateInput';
import AppDropdown from '../../../components/AppDropdown';
import AppCombobox from '../../../components/AppCombobox';
import { appToast } from '../../../components/AppToast';
import { fetchEmployeeOptions, fetchAccountHeadOptions } from '../../../api/filterOptionApi';

export default function LoansEntryScreen() {
  const navigate = useNavigate();
  const { state, actions } = useLoansEntry();
  const {
    formData,
    isEditMode,
    isLoading,
    isSaving,
    payHeadOptions,
    loanTypeOptions,
    employeeOptions,
    accountHeadOptions,
  } = state;

  const handleInfoClick = (label) => {
    if (label === 'Employee') {
      appToast.info('Employee account heads are linked to your Chart of Accounts under Account Group Employees.');
    } else if (label === 'Guarantor 1') {
      appToast.info('Guarantor 1 account head selected from Chart of Accounts (AcHeads Master).');
    } else if (label === 'Guarantor 2') {
      appToast.info('Guarantor 2 account head selected from Chart of Accounts (AcHeads Master).');
    } else {
      appToast.info(`Viewing details for ${label}`);
    }
  };

  const handleAddClick = (label) => {
    if (label === 'Employee') {
      navigate('/masters/acheads/heads/entry?group=Employees');
    } else {
      navigate('/masters/acheads/heads/entry');
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* Top Header Banner */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">
            {isEditMode ? 'Edit Loan Application' : 'Loan Application'}
          </h1>
        </div>

        <Link
          to="/payroll/loans"
          className="inline-flex items-center gap-1 text-xs font-bold text-on-surface-variant hover:text-primary transition"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          <span>Back to List</span>
        </Link>
      </section>

      {/* Main Form Card */}
      <div className="rounded-2xl border border-outline-variant/25 bg-surface-container-lowest overflow-hidden shadow-xs">
        {/* Card Header Bar */}
        <div className="border-b border-outline-variant/20 bg-surface-container-low/80 px-6 py-4">
          <h2 className="text-sm font-bold text-on-surface">Loan Application</h2>
        </div>

        {/* Card Form Body */}
        <div className="p-6 sm:p-8">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16 text-center text-xs text-on-surface-variant font-semibold gap-2">
              <span className="material-symbols-outlined text-xl animate-spin text-primary">
                progress_activity
              </span>
              Loading loan application details...
            </div>
          ) : (
            <div className="max-w-3xl space-y-4 text-xs">
              {/* 1. Apply Date */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 items-center">
                <label className="sm:col-span-3 font-bold text-on-surface-variant">
                  Apply Date
                </label>
                <div className="sm:col-span-9 max-w-md">
                  <AppDateInput
                    value={formData.applyDate}
                    onChange={(val) => actions.handleChange('applyDate', val)}
                    placeholder="dd/mm/yyyy"
                    inputClassName="h-9 text-xs font-semibold bg-surface-container-lowest rounded-xl border border-outline-variant/30 px-3 focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>

              {/* 2. Pay Head */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 items-center">
                <label className="sm:col-span-3 font-bold text-on-surface-variant">
                  Pay Head
                </label>
                <div className="sm:col-span-9 max-w-md">
                  <AppDropdown
                    options={payHeadOptions}
                    value={formData.payHead}
                    onChange={(val) => actions.handlePayHeadChange(val)}
                    placeholder="Select Pay Head"
                    triggerClassName="h-9 text-xs font-semibold bg-surface-container-lowest w-full rounded-xl border border-outline-variant/30 px-3 focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>

              {/* 3. Employee (AcHead under Account Group Employees) */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 items-center">
                <label className="sm:col-span-3 font-bold text-on-surface-variant">
                  Employee
                </label>
                <div className="sm:col-span-9 max-w-md flex items-center gap-1.5">
                  <div className="flex-1 min-w-0">
                    <AppCombobox
                      fetchOptions={fetchEmployeeOptions}
                      value={String(formData.emp_id || formData.name || '')}
                      initialLabel={formData.emp_name || formData.name || ''}
                      onChange={(val) => actions.handleEmployeeChange(val)}
                      placeholder="Select Employee (AcHead Group Employees)"
                      searchPlaceholder="Search employee name or code..."
                      triggerClassName="h-9 text-xs font-semibold bg-surface-container-lowest w-full rounded-xl border border-outline-variant/30 px-3 focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleInfoClick('Employee')}
                    aria-label="View Employee info"
                    title="Employee Account Head Info"
                    className="h-9 w-9 rounded-xl border border-outline-variant/30 bg-surface-container-low hover:bg-surface-container-high text-on-surface-variant flex items-center justify-center transition cursor-pointer shrink-0"
                  >
                    <span className="material-symbols-outlined text-base">info</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleAddClick('Employee')}
                    aria-label="Create new Employee Account Head"
                    title="Create new Account Head"
                    className="h-9 w-9 rounded-xl border border-outline-variant/30 bg-surface-container-low hover:bg-surface-container-high text-on-surface-variant flex items-center justify-center transition cursor-pointer shrink-0"
                  >
                    <span className="material-symbols-outlined text-base">add</span>
                  </button>
                </div>
              </div>

              {/* 4. Loan Date */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 items-center">
                <label className="sm:col-span-3 font-bold text-on-surface-variant">
                  Loan Date
                </label>
                <div className="sm:col-span-9 max-w-md">
                  <AppDateInput
                    value={formData.loanDate}
                    onChange={(val) => actions.handleChange('loanDate', val)}
                    placeholder="dd/mm/yyyy"
                    inputClassName="h-9 text-xs font-semibold bg-surface-container-lowest rounded-xl border border-outline-variant/30 px-3 focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>

              {/* 5. Loan Type */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 items-center">
                <label className="sm:col-span-3 font-bold text-on-surface-variant">
                  Loan Type
                </label>
                <div className="sm:col-span-9 max-w-md">
                  <AppDropdown
                    options={loanTypeOptions}
                    value={formData.loanType}
                    onChange={(val) => actions.handleLoanTypeChange(val)}
                    placeholder="Select Loan Type"
                    triggerClassName="h-9 text-xs font-semibold bg-surface-container-lowest w-full rounded-xl border border-outline-variant/30 px-3 focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>

              {/* 6. Loan Amount */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 items-center">
                <label className="sm:col-span-3 font-bold text-on-surface-variant">
                  Loan Amount
                </label>
                <div className="sm:col-span-9 max-w-md">
                  <input
                    type="number"
                    value={formData.loanAmount}
                    onChange={(e) => actions.handleChange('loanAmount', e.target.value)}
                    placeholder="0"
                    className="h-9 w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 text-xs font-semibold text-on-surface outline-none focus:ring-2 focus:ring-primary/20 tabular-nums"
                  />
                </div>
              </div>

              {/* 7. Guarantor Name1 (AcHead) */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 items-center">
                <label className="sm:col-span-3 font-bold text-on-surface-variant">
                  Guarantor Name1
                </label>
                <div className="sm:col-span-9 max-w-md flex items-center gap-1.5">
                  <div className="flex-1 min-w-0">
                    <AppCombobox
                      fetchOptions={fetchAccountHeadOptions}
                      value={String(formData.guarantor1_id || formData.guarantorName1 || '')}
                      initialLabel={formData.guarantorName1 || ''}
                      onChange={(val) => actions.handleGuarantor1Change(val)}
                      placeholder="Select Guarantor 1 (AcHead)"
                      searchPlaceholder="Search account head..."
                      triggerClassName="h-9 text-xs font-semibold bg-surface-container-lowest w-full rounded-xl border border-outline-variant/30 px-3 focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleInfoClick('Guarantor 1')}
                    aria-label="View Guarantor 1 info"
                    title="Guarantor 1 Account Head Info"
                    className="h-9 w-9 rounded-xl border border-outline-variant/30 bg-surface-container-low hover:bg-surface-container-high text-on-surface-variant flex items-center justify-center transition cursor-pointer shrink-0"
                  >
                    <span className="material-symbols-outlined text-base">info</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleAddClick('Guarantor 1')}
                    aria-label="Create new Guarantor 1 Account Head"
                    title="Create new Account Head"
                    className="h-9 w-9 rounded-xl border border-outline-variant/30 bg-surface-container-low hover:bg-surface-container-high text-on-surface-variant flex items-center justify-center transition cursor-pointer shrink-0"
                  >
                    <span className="material-symbols-outlined text-base">add</span>
                  </button>
                </div>
              </div>

              {/* 8. Guarantor Name2 (AcHead) */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 items-center">
                <label className="sm:col-span-3 font-bold text-on-surface-variant">
                  Guarantor Name2
                </label>
                <div className="sm:col-span-9 max-w-md flex items-center gap-1.5">
                  <div className="flex-1 min-w-0">
                    <AppCombobox
                      fetchOptions={fetchAccountHeadOptions}
                      value={String(formData.guarantor2_id || formData.guarantorName2 || '')}
                      initialLabel={formData.guarantorName2 || ''}
                      onChange={(val) => actions.handleGuarantor2Change(val)}
                      placeholder="Select Guarantor 2 (AcHead)"
                      searchPlaceholder="Search account head..."
                      triggerClassName="h-9 text-xs font-semibold bg-surface-container-lowest w-full rounded-xl border border-outline-variant/30 px-3 focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleInfoClick('Guarantor 2')}
                    aria-label="View Guarantor 2 info"
                    title="Guarantor 2 Account Head Info"
                    className="h-9 w-9 rounded-xl border border-outline-variant/30 bg-surface-container-low hover:bg-surface-container-high text-on-surface-variant flex items-center justify-center transition cursor-pointer shrink-0"
                  >
                    <span className="material-symbols-outlined text-base">info</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleAddClick('Guarantor 2')}
                    aria-label="Create new Guarantor 2 Account Head"
                    title="Create new Account Head"
                    className="h-9 w-9 rounded-xl border border-outline-variant/30 bg-surface-container-low hover:bg-surface-container-high text-on-surface-variant flex items-center justify-center transition cursor-pointer shrink-0"
                  >
                    <span className="material-symbols-outlined text-base">add</span>
                  </button>
                </div>
              </div>

              {/* 9. EMI FromDate */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 items-center">
                <label className="sm:col-span-3 font-bold text-on-surface-variant">
                  EMI FromDate
                </label>
                <div className="sm:col-span-9 max-w-md">
                  <AppDateInput
                    value={formData.emiFromDate}
                    onChange={(val) => actions.handleChange('emiFromDate', val)}
                    placeholder="dd/mm/yyyy"
                    inputClassName="h-9 text-xs font-semibold bg-surface-container-lowest rounded-xl border border-outline-variant/30 px-3 focus:ring-2 focus:ring-primary/20"
                  />
                </div>
              </div>

              {/* 10. EMI Amount */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 items-center">
                <label className="sm:col-span-3 font-bold text-on-surface-variant">
                  EMI Amount
                </label>
                <div className="sm:col-span-9 max-w-md">
                  <input
                    type="number"
                    value={formData.emiAmount}
                    onChange={(e) => actions.handleChange('emiAmount', e.target.value)}
                    placeholder="0"
                    className="h-9 w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 text-xs font-semibold text-on-surface outline-none focus:ring-2 focus:ring-primary/20 tabular-nums"
                  />
                </div>
              </div>

              {/* 11. Remarks */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 items-start">
                <label className="sm:col-span-3 font-bold text-on-surface-variant pt-2">
                  Remarks
                </label>
                <div className="sm:col-span-9 max-w-md">
                  <textarea
                    rows={3}
                    value={formData.remarks}
                    onChange={(e) => actions.handleChange('remarks', e.target.value)}
                    placeholder="Enter any additional remarks or repayment conditions..."
                    className="w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-3 text-xs text-on-surface outline-none focus:ring-2 focus:ring-primary/20 resize-y"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Bottom-Right Action Buttons Bar */}
        <div className="flex flex-wrap items-center justify-end gap-2.5 border-t border-outline-variant/15 bg-surface-container-low/40 px-6 py-4">
          <button
            type="button"
            onClick={actions.handleApproveAndExit}
            disabled={isSaving || isLoading}
            className="h-9 px-5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-xs transition shadow-xs flex items-center gap-1.5 cursor-pointer disabled:opacity-60"
          >
            <span className="material-symbols-outlined text-sm">check_circle</span>
            <span>Approve &amp; eXit</span>
          </button>

          <button
            type="button"
            onClick={actions.handleRejectAndExit}
            disabled={isSaving || isLoading}
            className="h-9 px-5 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-xl text-xs transition shadow-xs flex items-center gap-1.5 cursor-pointer disabled:opacity-60"
          >
            <span className="material-symbols-outlined text-sm">cancel</span>
            <span>Reject &amp; eXit</span>
          </button>

          <button
            type="button"
            onClick={actions.handleSaveAndExit}
            disabled={isSaving || isLoading}
            className="h-9 px-5 bg-primary hover:bg-primary/90 text-on-primary font-bold rounded-xl text-xs transition shadow-xs flex items-center gap-1.5 cursor-pointer disabled:opacity-60"
          >
            <span className="material-symbols-outlined text-sm">save</span>
            <span>Save &amp; eXit</span>
          </button>

          <button
            type="button"
            onClick={actions.handleCancel}
            disabled={isSaving}
            className="h-9 px-5 bg-surface-container-high hover:bg-surface-container-highest text-on-surface font-bold rounded-xl text-xs transition cursor-pointer border border-outline-variant/20"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
